<script setup lang="ts">
/*
 * Triggered from the user-menu's "Change password" item. Calls Amplify
 * updatePassword() while the user is signed in. Does NOT sign the user
 * out on success.
 */
import { ref, watch } from 'vue';
import { updatePassword } from 'aws-amplify/auth';
import { Button, FormField, Modal, TextInput } from '@/ui';
import { useToast } from '@/composables/useToast';

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

const oldPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const error = ref('');
const submitting = ref(false);
const toast = useToast();

watch(
  () => props.open,
  (open) => {
    if (open) {
      oldPassword.value = '';
      newPassword.value = '';
      confirmPassword.value = '';
      error.value = '';
    }
  },
);

async function submit() {
  error.value = '';
  if (!oldPassword.value || !newPassword.value) {
    error.value = 'All fields are required';
    return;
  }
  if (newPassword.value !== confirmPassword.value) {
    error.value = 'New passwords do not match';
    return;
  }
  submitting.value = true;
  try {
    await updatePassword({ oldPassword: oldPassword.value, newPassword: newPassword.value });
    toast.success('Password updated');
    emit('close');
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Could not update password';
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <Modal :open="open" title="Change password" size="sm" @close="$emit('close')">
    <form class="flex flex-col gap-4" @submit.prevent="submit">
      <FormField label="Current password" required>
        <TextInput v-model="oldPassword" type="password" autocomplete="current-password" />
      </FormField>
      <FormField label="New password" required>
        <TextInput v-model="newPassword" type="password" autocomplete="new-password" />
      </FormField>
      <FormField label="Confirm new password" :error="error" required>
        <TextInput v-model="confirmPassword" type="password" autocomplete="new-password" />
      </FormField>
    </form>
    <template #footer>
      <Button variant="secondary" @click="$emit('close')">Cancel</Button>
      <Button :disabled="submitting" @click="submit">
        {{ submitting ? 'Saving…' : 'Save' }}
      </Button>
    </template>
  </Modal>
</template>
